import React, { useCallback, useState } from 'react';
import { AlertCircleIcon, RefreshCwIcon } from 'lucide-react';
import { PRIVACY_URL } from '../data/site';

/*
 * NOTE FOR HANDOFF: the verification control below is a LOCAL visual mockup for
 * design review only. When this page is transferred into Website Forge, wire a
 * real CAPTCHA (e.g. reCAPTCHA v2 with a production site key) and verify the
 * token server-side, and connect the submit handler to the real lead endpoint.
 */

const SERVICE_OPTIONS = ['Auto Glass', 'Residential Glass', 'Glass Shower Doors', 'Commercial Glass'];
interface Values {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
}
const EMPTY: Values = {
  name: '',
  email: '',
  phone: '',
  service: '',
  message: ''
};
type Errors = Partial<Record<keyof Values | 'captcha', string>>;
interface QuoteFormProps {
  idPrefix: string;
  initialService?: string;
  title?: string;
  description?: string;
  className?: string;
  headingLevel?: 2 | 3;
}
const fieldClass = 'box-border block w-full rounded border border-[#858585] bg-[#fafafa] px-3 py-[7px] text-[16px] leading-6 text-kosin-ink placeholder:text-kosin-body hover:border-[#616161] focus:border-kosin-red focus:bg-white aria-[invalid=true]:border-kosin-red';
const labelClass = 'block text-[13px] font-semibold text-kosin-charcoal';
export function QuoteForm({
  idPrefix,
  initialService = '',
  title = 'Request A Free Quote',
  description = '',
  className = '',
  headingLevel = 2
}: QuoteFormProps) {
  const Heading = (headingLevel === 3 ? 'h3' : 'h2') as 'h2' | 'h3';
  const [values, setValues] = useState<Values>(() => ({
    ...EMPTY,
    service: SERVICE_OPTIONS.includes(initialService) ? initialService : ''
  }));
  const [errors, setErrors] = useState<Errors>({});
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const id = (name: string) => `${idPrefix}-${name}`;
  const update = useCallback((field: keyof Values, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  }, []);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next: Errors = {};
    if (!values.name.trim()) next.name = 'Please enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) next.email = 'Please enter a valid email address.';
    if (values.phone.replace(/\D/g, '').length < 10) next.phone = 'Please enter a 10-digit phone number.';
    if (!values.service) next.service = 'Please choose a service.';
    if (!captchaChecked) next.captcha = 'Please confirm you are not a robot.';
    setErrors(next);
    if (Object.keys(next).length > 0) {
      const firstField = Object.keys(next).find(key => key !== 'captcha');
      if (firstField) document.getElementById(id(firstField))?.focus();
    }
    /* No network request is made from this design review build. */
  };
  const errorText = (key: keyof Errors) => errors[key] ? <p id={id(`${key}-error`)} role="alert" className="mt-1 flex items-start gap-1 text-[12px] text-kosin-red" data-id="element-283" data-mp="3sa5chg3mjvcf">
        <AlertCircleIcon className="mt-[2px] h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-284" data-mp="14doam9eqss9" />
        {errors[key]}
      </p> : null;
  return <div className={`box-border w-full rounded bg-white p-4 shadow-[0_10px_30px_rgba(22,22,22,0.14)] sm:p-5 ${className}`} data-id="element-285" data-mp="3nh2g707gq7sj">
      <Heading className="text-[19px] font-bold leading-tight text-kosin-charcoal" data-id="element-286" data-mp="39h1g3mz0v4e4">
        {title}
      </Heading>
      {description && <p className="mt-1 text-[13px] leading-snug text-kosin-body" data-id="element-287" data-mp="30i69i9s2fsaa">
          {description}
        </p>}

      <form id={id('form')} className="box-border mt-3 w-full" noValidate onSubmit={handleSubmit} data-id="element-288" data-mp="34ihu5mkfhslr">
        <div className="space-y-2" data-id="element-289" data-mp="yq1mncpfbl6f">
          <div data-id="element-290" data-mp="yr2w5cqsjzm1">
            <label className={labelClass} htmlFor={id('name')} data-id="element-291" data-mp="yogrplz76081">
              Full Name
            </label>
            <input id={id('name')} name="name" type="text" autoComplete="name" required value={values.name} onChange={e => update('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? id('name-error') : undefined} className={`mt-1 ${fieldClass}`} data-id="element-292" data-mp="e18rmtaoozs2" />
            {errorText('name')}
          </div>

          <div className="grid grid-cols-1 gap-2.5 min-[420px]:grid-cols-2" data-id="element-293" data-mp="3r9lmw8ffvcfi">
            <div data-id="element-294" data-mp="3mfr55aje97j2">
              <label className={labelClass} htmlFor={id('email')} data-id="element-295" data-mp="3mf7m1qi0cd3g">
                Email Address
              </label>
              <input id={id('email')} name="email" type="email" autoComplete="email" required value={values.email} onChange={e => update('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? id('email-error') : undefined} className={`mt-1 ${fieldClass}`} data-id="element-296" data-mp="3cj4lgp2t0mim" />
              {errorText('email')}
            </div>
            <div data-id="element-297" data-mp="1s0k8ext5855y">
              <label className={labelClass} htmlFor={id('phone')} data-id="element-298" data-mp="1s253cojddq4c">
                Phone Number
              </label>
              <input id={id('phone')} name="phone" type="tel" autoComplete="tel" required value={values.phone} onChange={e => update('phone', e.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? id('phone-error') : undefined} className={`mt-1 ${fieldClass}`} data-id="element-299" data-mp="1wz5a71o1mwwi" />
              {errorText('phone')}
            </div>
          </div>

          <div data-id="element-300" data-mp="lkhyofvpwmdy">
            <label className={labelClass} htmlFor={id('service')} data-id="element-301" data-mp="ljzvt0ejv9x2">
              What Are You Interested In?
            </label>
            <select id={id('service')} name="service" required value={values.service} onChange={e => update('service', e.target.value)} aria-invalid={Boolean(errors.service)} aria-describedby={errors.service ? id('service-error') : undefined} className={`mt-1 ${fieldClass}`} data-id="element-302" data-mp="diiugl1dsmuf">
              <option value="" data-id="element-303" data-mp="3qqvojwuegtga">Select a service</option>
              {SERVICE_OPTIONS.map(option => <option key={option} value={option} data-id="element-304" data-mp="3mr3bp7xz4154">
                  {option}
                </option>)}
            </select>
            {errorText('service')}
          </div>

          <div data-id="element-305" data-mp="3vodlspz21k8g">
            <label className={labelClass} htmlFor={id('message')} data-id="element-306" data-mp="3vs1hnvrz2iqc">
              Message <span className="font-normal text-kosin-body" data-id="element-307" data-mp="1fwzm937f115d">(optional)</span>
            </label>
            <textarea id={id('message')} name="message" rows={2} value={values.message} onChange={e => update('message', e.target.value)} className={`mt-1 h-[58px] resize-y !leading-5 ${fieldClass}`} data-id="element-308" data-mp="1ayxzo8as6jp5" />
          </div>
        </div>

        <div className="mt-3" data-id="element-309" data-mp="2jyr47o9s7ibe">
          <div className="box-border flex w-full max-w-[304px] items-center justify-between gap-3 rounded border border-[#d3d3d3] bg-[#f9f9f9] px-3 py-[15px]" data-id="element-310" data-mp="2ouzfurorstdt">
            <div className="flex items-center gap-3" data-id="element-311" data-mp="3cnfv6uh4i79f">
              <input id={id('captcha')} name="captcha" type="checkbox" checked={captchaChecked} onChange={e => {
              setCaptchaChecked(e.target.checked);
              setErrors(prev => ({
                ...prev,
                captcha: undefined
              }));
            }} aria-invalid={Boolean(errors.captcha)} aria-describedby={errors.captcha ? id('captcha-error') : undefined} className="h-6 w-6 shrink-0 cursor-pointer rounded-[2px] border border-[#c1c1c1] accent-[#1a73e8]" data-id="element-312" data-mp="3gllz7uqku8qn" />
              <label htmlFor={id('captcha')} className="cursor-pointer text-[14px] text-kosin-ink" data-id="element-313" data-mp="1vgwr3558rl9n">
                I&apos;m not a robot
              </label>
            </div>
            <div className="flex shrink-0 flex-col items-center gap-0.5 text-[#9aa0a6]" data-id="element-314" data-mp="1lhfkmli613cm">
              <RefreshCwIcon className="h-5 w-5 text-[#1a73e8]" aria-hidden="true" data-id="element-315" data-mp="2j6hk6gv3zywn" />
              <span className="text-[10px] leading-none" data-id="element-316" data-mp="2e9halvz1we0s">reCAPTCHA</span>
            </div>
          </div>
          {errorText('captcha')}
        </div>

        <button type="submit" className="mt-3 w-full rounded bg-kosin-red px-5 py-2.5 text-[16px] font-semibold text-white hover:bg-kosin-bright transition-colors duration-150 ease-kosin" data-id="element-317" data-mp="257rmp04dnx7r">
          Request My Free Quote
        </button>

        <p className="mt-2.5 text-[12px] text-kosin-body" data-id="element-318" data-mp="tuffqgdbmyhd">
          <a href={PRIVACY_URL} className="font-semibold text-kosin-charcoal underline underline-offset-2 hover:text-kosin-red transition-colors duration-150 ease-kosin" data-id="element-319" data-mp="35gpo4h7m8lma">
            Privacy Policy
          </a>
        </p>
      </form>
    </div>;
}